import React, { useState, useEffect, useCallback } from "react";
import { signOut, fetchAuthSession, getCurrentUser } from "aws-amplify/auth";

let logoutTimer;
let refreshTimer;

const cognitoLogout = async () => {
  try {
    return await signOut();
  } catch (error) {
    // signOut() throws "Auth UserPool not configured" when Amplify hasn't been
    // configured yet (e.g. tenant metadata not loaded) or when no user is
    // signed in. Logout only needs to clear the Cognito session, so there is
    // nothing to do in that case — swallow it instead of crashing the app.
    // Callers still clear local session state (token, localStorage) themselves.
    console.warn(
      "cognitoLogout: skipping signOut -",
      error?.message || error,
    );
  }
};

export const AuthContext = React.createContext({
  token: null,
  isLoggedIn: () => {},
  currentUser: null,
  role: "",
  login: async () => {},
  logout: async () => {},
  userfetch: async () => {},
});

const calculateRemainingTime = (expirationTime) => {
  const currentTime = new Date().getTime();
  const adjExpirationTime = new Date(expirationTime).getTime();

  const remainingDuration = adjExpirationTime - currentTime;

  return remainingDuration;
};

const retrieveStoredToken = () => {
  let storedToken = localStorage.getItem("token");
  let storedExpirationDate = localStorage.getItem("expirationTime");
  let storeLoggedInUser = localStorage.getItem("loggedInUser");

  let remainingTime = calculateRemainingTime(storedExpirationDate);

  if (remainingTime <= -30000) {
    if (storedToken !== null && storedToken !== "") {
      cognitoLogout();
      localStorage.removeItem("token");
      localStorage.removeItem("expirationTime");
      localStorage.removeItem("loggedInUser");
      localStorage.removeItem("accessToken");
    }
    return null;
  }

  return {
    token: storedToken,
    duration: remainingTime,
    loggedinUser: storeLoggedInUser,
  };
};

export const AuthContextProvider = (props) => {
  let tokenData = retrieveStoredToken();
  let initialToken;
  let initialLoggedInUser;
  let initialLoggedInRole;
  if (tokenData) {
    initialToken = tokenData.token;
    initialLoggedInUser = tokenData.loggedinUser;
    initialLoggedInRole = JSON.parse(tokenData.token)?.payload.scope;
  }

  let [token, setToken] = useState(initialToken);

  let [loggedInRole, setLoggedInRole] = useState(initialLoggedInRole);
  let [loggedInUser, setLoggedInUser] = useState(initialLoggedInUser);

  const userIsLoggedIn = () => {
    tokenData = retrieveStoredToken();
    if (tokenData) {
      return true;
    } else {
      return false;
    }
  };

  const logoutHandler = useCallback(async () => {
    await cognitoLogout();
    setToken(null);
    setLoggedInUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("expirationTime");
    localStorage.removeItem("loggedInUser");
    localStorage.removeItem("accessToken");
    // localStorage.removeItem("appConfig");

    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
  }, []);

  const currentAuthenticatedUser = async () => {
    const { username, signInDetails } = await getCurrentUser();

    const { tokens: session } = await fetchAuthSession();

    // Note that session will no longer contain refreshToken and clockDrift
    return {
      username,
      session,
      authenticationFlowType: signInDetails.authFlowType,
    };
  };

  //original code
  // const checkForReFreshToken = useCallback(async () => {
  //   try {
  //     let data = await currentAuthenticatedUser();
  //     console.log("checkForReFreshToken :: " + data);
  //     if (data != null) {
  //       const expirationTime = new Date(
  //         data.signInUserSession.accessToken.payload.exp * 1000,
  //       );

  //       localStorage.setItem(
  //         "token",
  //         JSON.stringify(data.signInUserSession.accessToken),
  //       );
  //       localStorage.setItem("expirationTime", expirationTime.toISOString());
  //       localStorage.setItem("loggedInUser", JSON.stringify(data));
  //       localStorage.setItem(
  //         "accessToken",
  //         data.signInUserSession.accessToken.jwtToken,
  //       );
  //       const remainingTime = calculateRemainingTime(
  //         expirationTime.toISOString(),
  //       );
  //       clearTimeout(refreshTimer);
  //       refreshTimer = setTimeout(checkForReFreshToken, remainingTime);
  //       clearTimeout(logoutTimer);
  //       let logoutTimerCal = remainingTime + 30000;
  //       logoutTimer = setTimeout(logoutHandler, logoutTimerCal);
  //     }
  //   } catch (e) {
  //     console.log("checkForReFreshToken Exception: " + e);
  //   }
  // }, [logoutHandler]);

  // const loginHandler = async (
  //   accessToken,
  //   expirationTime,
  //   loggedInUser,
  //   jwtToken,
  // ) => {
  //   setToken(JSON.stringify(accessToken));
  //   setLoggedInUser(JSON.stringify(loggedInUser));
  //   setLoggedInRole(JSON.stringify(accessToken.payload.scope));
  //   localStorage.setItem("token", JSON.stringify(accessToken));
  //   localStorage.setItem("expirationTime", expirationTime);
  //   localStorage.setItem("loggedInUser", JSON.stringify(loggedInUser));
  //   localStorage.setItem("accessToken", jwtToken);

  //   const remainingTime = calculateRemainingTime(expirationTime);

  //   refreshTimer = setTimeout(checkForReFreshToken, remainingTime);
  //   let logoutTimerCal = remainingTime + 30000;
  //   logoutTimer = setTimeout(logoutHandler, logoutTimerCal);
  // };

  const checkForReFreshToken = useCallback(async () => {
    try {
      let data = await currentAuthenticatedUser();
      console.log("checkForReFreshToken :: " + data);

      if (data != null && data.session?.accessToken) {
        const accessToken = data.session.accessToken;

        const expirationTime = new Date(accessToken.payload.exp * 1000);

        // Update state and localStorage with new token and user data
        setToken(JSON.stringify(accessToken));
        setLoggedInUser(JSON.stringify(data));
        setLoggedInRole(accessToken.payload.scope);

        localStorage.setItem("token", JSON.stringify(accessToken));
        localStorage.setItem("expirationTime", expirationTime.toISOString());
        localStorage.setItem("loggedInUser", JSON.stringify(data));
        localStorage.setItem("accessToken", accessToken.toString());

        const remainingTime = calculateRemainingTime(
          expirationTime.toISOString()
        );
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(checkForReFreshToken, remainingTime);
        clearTimeout(logoutTimer);
        let logoutTimerCal = remainingTime + 30000;
        logoutTimer = setTimeout(logoutHandler, logoutTimerCal);
      }
    } catch (e) {
      console.log("checkForReFreshToken Exception: " + e);
    }
  }, [logoutHandler]);

  const loginHandler = async (
    accessToken,
    expirationTime,
    loggedInUser,
    jwtToken
  ) => {
    // Get session from Amplify
    const { tokens } = await fetchAuthSession();

    const userWithSession = {
      ...loggedInUser,
      session: tokens,
    };

    setToken(JSON.stringify(accessToken));
    setLoggedInUser(JSON.stringify(userWithSession));
    setLoggedInRole(accessToken.payload.scope);
    localStorage.setItem("token", JSON.stringify(accessToken));
    localStorage.setItem("expirationTime", expirationTime);
    localStorage.setItem("loggedInUser", JSON.stringify(userWithSession));
    localStorage.setItem("accessToken", jwtToken);

    const remainingTime = calculateRemainingTime(expirationTime);

    refreshTimer = setTimeout(checkForReFreshToken, remainingTime);
    let logoutTimerCal = remainingTime + 30000;
    logoutTimer = setTimeout(logoutHandler, logoutTimerCal);
  };

  const userfetchHandler = async () => {
    //const userr = await getCurrentUser();
    const user = await fetchAuthSession();

    if (user.tokens === undefined) {
      return false;
    }

    const accessToken = user.tokens.accessToken.toString();

    const storedAccessToken = localStorage.getItem("accessToken");
    if (accessToken !== storedAccessToken) {
      localStorage.setItem("accessToken", accessToken);
      window.location.reload();
    }

    return true;
  };

  useEffect(() => {
    if (tokenData) {
      let logoutTimerCal = tokenData.duration + 30000;

      if (logoutTimer) {
        clearTimeout(logoutTimer);
      }
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
      refreshTimer = setTimeout(checkForReFreshToken, tokenData.duration);
      logoutTimer = setTimeout(logoutHandler, logoutTimerCal);
    } else {
      // If localStorage tokens are missing but Cognito session exists
      // attempt to restore the session using refresh token
      checkForReFreshToken();
    }
  }, [tokenData, logoutHandler, checkForReFreshToken]);

  let contextValue = {
    token: token !== undefined ? JSON.parse(token) : token,
    isLoggedIn: userIsLoggedIn,
    currentUser:
      loggedInUser !== undefined ? JSON.parse(loggedInUser) : loggedInUser,
    role: loggedInRole,
    login: loginHandler,
    logout: logoutHandler,
    userfetch: userfetchHandler,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
